/**
 * Digest panel — KAIROS staging queue UI shell.
 *
 * Displays pending proposals from the staging queue, grouped by type.
 * Analyst can accept or reject each proposal. Full interactivity
 * comes when KAIROS is built — this is the data model and shell.
 */

import { escapeHtml } from '@/utils/sanitize';
import {
  getPendingProposals,
  getPendingCount,
  resolveProposal,
} from '@/intelligence/investigation/staging-store';
import type { StagingProposal } from '@/intelligence/investigation/staging-store';

// ---------------------------------------------------------------------------
// Proposal type labels
// ---------------------------------------------------------------------------

const PROPOSAL_TYPE_LABELS: Record<string, string> = {
  cross_link: 'Cross-Investigation Links',
  contradiction: 'Source Contradictions',
  dead_thread: 'Stale Investigations',
  pattern_elevation: 'Pattern Elevations',
};

const PROPOSAL_TYPE_COLORS: Record<string, string> = {
  cross_link: '#00b4d8',
  contradiction: '#ef4444',
  dead_thread: '#64748b',
  pattern_elevation: '#f59e0b',
};

// ---------------------------------------------------------------------------
// DigestPanel
// ---------------------------------------------------------------------------

export class DigestPanel {
  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
    this.container.style.cssText = `
      flex: 1; display: flex; flex-direction: column; overflow: hidden;
      font-family: 'JetBrains Mono', 'Cascadia Code', monospace;
    `;
    this.render();
  }

  get element(): HTMLElement {
    return this.container;
  }

  async render(): Promise<void> {
    const count = await getPendingCount();
    const proposals = await getPendingProposals();

    this.container.innerHTML = '';

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      padding: 10px 12px; border-bottom: 1px solid var(--border-primary, #1a1f2e);
      display: flex; align-items: center; justify-content: space-between;
    `;

    const titleEl = document.createElement('span');
    titleEl.style.cssText = 'font-size: 10px; font-weight: 600; color: var(--text-primary, #e2e8f0); letter-spacing: 0.5px;';
    titleEl.textContent = 'INTELLIGENCE DIGEST';
    header.appendChild(titleEl);

    const badge = document.createElement('span');
    badge.style.cssText = `
      font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 8px;
      background: ${count > 0 ? '#f59e0b33' : '#1e293b'}; color: ${count > 0 ? '#f59e0b' : '#475569'};
    `;
    badge.textContent = String(count);
    header.appendChild(badge);

    this.container.appendChild(header);

    // Content area
    const content = document.createElement('div');
    content.style.cssText = 'flex: 1; overflow-y: auto; padding: 0;';

    if (proposals.length === 0) {
      const empty = document.createElement('div');
      empty.style.cssText = 'padding: 24px 12px; text-align: center;';

      const emptyText = document.createElement('div');
      emptyText.style.cssText = 'font-size: 10px; color: #475569; line-height: 1.6;';
      emptyText.textContent = 'No pending proposals. KAIROS will surface connections when investigations are idle.';
      empty.appendChild(emptyText);

      content.appendChild(empty);
    } else {
      // Group by proposal type
      const grouped = new Map<string, StagingProposal[]>();
      for (const p of proposals) {
        const group = grouped.get(p.proposalType) || [];
        group.push(p);
        grouped.set(p.proposalType, group);
      }

      for (const [type, items] of grouped) {
        const section = document.createElement('div');
        section.style.cssText = 'margin-bottom: 2px;';

        // Section header
        const sectionHeader = document.createElement('div');
        sectionHeader.style.cssText = `
          padding: 6px 12px; background: #0a0e17;
          border-bottom: 1px solid #1e293b;
        `;
        const sectionTitle = document.createElement('span');
        sectionTitle.style.cssText = `font-size: 8px; font-weight: 600; color: ${PROPOSAL_TYPE_COLORS[type] || '#64748b'}; letter-spacing: 0.5px;`;
        sectionTitle.textContent = (PROPOSAL_TYPE_LABELS[type] || type).toUpperCase();
        sectionHeader.appendChild(sectionTitle);
        section.appendChild(sectionHeader);

        // Cards
        for (const proposal of items) {
          section.appendChild(this.buildProposalCard(proposal));
        }

        content.appendChild(section);
      }
    }

    this.container.appendChild(content);
  }

  private buildProposalCard(proposal: StagingProposal): HTMLElement {
    const card = document.createElement('div');
    card.style.cssText = `
      padding: 8px 12px; border-bottom: 1px solid #1e293b;
    `;

    // Summary
    const summary = document.createElement('div');
    summary.style.cssText = 'font-size: 10px; color: var(--text-primary, #e2e8f0); margin-bottom: 4px; line-height: 1.4;';
    summary.textContent = escapeHtml(proposal.summary);
    card.appendChild(summary);

    // Confidence
    const meta = document.createElement('div');
    meta.style.cssText = 'font-size: 8px; color: #475569; margin-bottom: 6px;';
    meta.textContent = `Confidence: ${(proposal.confidence * 100).toFixed(0)}% | Tier ${proposal.tier}`;
    card.appendChild(meta);

    // Actions
    const actions = document.createElement('div');
    actions.style.cssText = 'display: flex; gap: 6px;';

    const acceptBtn = document.createElement('button');
    acceptBtn.style.cssText = `
      font-size: 8px; padding: 2px 8px; border: 1px solid #22c55e; border-radius: 2px;
      background: transparent; color: #22c55e; cursor: pointer;
      font-family: 'JetBrains Mono', monospace; letter-spacing: 0.5px;
    `;
    acceptBtn.textContent = 'ACCEPT';
    acceptBtn.addEventListener('click', () => this.handleResolve(proposal.id, 'accepted'));
    actions.appendChild(acceptBtn);

    const rejectBtn = document.createElement('button');
    rejectBtn.style.cssText = `
      font-size: 8px; padding: 2px 8px; border: 1px solid #ef4444; border-radius: 2px;
      background: transparent; color: #ef4444; cursor: pointer;
      font-family: 'JetBrains Mono', monospace; letter-spacing: 0.5px;
    `;
    rejectBtn.textContent = 'REJECT';
    rejectBtn.addEventListener('click', () => this.handleResolve(proposal.id, 'rejected'));
    actions.appendChild(rejectBtn);

    card.appendChild(actions);
    return card;
  }

  private async handleResolve(proposalId: string, status: 'accepted' | 'rejected'): Promise<void> {
    await resolveProposal(proposalId, status, 'analyst');
    await this.render();
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

let _instance: DigestPanel | null = null;

/** Create the digest panel for mounting. */
export function createDigestPanel(container?: HTMLElement): DigestPanel {
  const el = container ?? document.createElement('div');
  if (!_instance) {
    _instance = new DigestPanel(el);
  }
  return _instance;
}

/** Destroy the digest panel. */
export function destroyDigestPanel(): void {
  if (_instance) {
    _instance.destroy();
    _instance = null;
  }
}
